// Reports whose data source is the published schedule (API_get_schedule /
// API_get_actual_used_schedule) or timecards (API_get_timecard_hours_split).
// These are hidden when a store has scheduling disabled, because the schedule
// and Time & Payroll surfaces they read are hidden by the master flag. NOT
// listed (kept available): Log Entries by Type (logbook) and Labor Metric Graph
// (routes through the already scheduling-gated calculate_metric endpoint).
const SCHEDULING_BACKED_REPORT_IDS = new Set([
  "labor-flags",
  "weekly-simple-schedule",
  "weekly-labor-schedule-detail",
  "daily-schedule-preview",
  "daily-labor-schedule-summary",
  "weekly-labor-schedule-summary",
  "labor-scheduling-roster",
  "punch-information",
  "labor-summary",
  "daily-punch-audit",
  "payroll-report",
  "autoschedule-evaluation",
  "autoschedule-role-profile",
  "autoschedule-eval-runs",
  "daily-labor-recap",
  "weekly-labor-recap",
]);

export function reportRequiresScheduling(report) {
  return !!report && SCHEDULING_BACKED_REPORT_IDS.has(report.id);
}

// Memberships that may run manager-grade reports. The Report Library is a
// manager surface (see NavigationSideBar roles) and its heaviest endpoints
// (get_timecard_hours_split, get_actual_used_schedule, get_effective_wages,
// calculate_metric) enforce UserIsStoreManager per store_id, so the store
// picker must not offer stores where the user is only an employee.
const MANAGER_PERMISSION_TYPES = new Set([
  "company_manager",
  "company_admin",
  "rightwork_internal",
]);

// The stores a report may run against, shared by the display (picker options
// + no-eligible-store block) and the landing page (tile visibility) so the
// two can't drift:
//   1. stores where the user holds a manager+ membership (skipped while
//      storeOptions hasn't loaded),
//   2. minus scheduling-disabled stores for scheduling-backed reports,
//   3. minus stores missing the report's requiredStoreFlag module,
//   4. for requireActiveStore reports, minus stores not open and fully
//      onboarded and stores where the manager membership is archived.
// The soft preferredStoreFlag is deliberately NOT applied here — it only
// reorders/narrows the picker while capable stores exist and never makes a
// store ineligible.
export function getEligibleStoresForReport(report, storeNames, storeOptions) {
  let stores = Array.isArray(storeNames) ? storeNames : [];

  const memberships = Array.isArray(storeOptions) ? storeOptions : [];
  if (memberships.length > 0) {
    const managerStoreIds = new Set(
      memberships
        .filter((m) => MANAGER_PERMISSION_TYPES.has(m.permission_type))
        .map((m) => m.store)
    );
    stores = stores.filter((s) => managerStoreIds.has(s.id));
    // The recap endpoint also ignores archived memberships. Scoped to these
    // reports: the store-scoped endpoints behind the others still accept them.
    if (report?.requireActiveStore) {
      const activeManagerStoreIds = new Set(
        memberships
          .filter(
            (m) => MANAGER_PERMISSION_TYPES.has(m.permission_type) && m.status !== "archived"
          )
          .map((m) => m.store)
      );
      stores = stores.filter((s) => activeManagerStoreIds.has(s.id));
    }
  }

  if (report && reportRequiresScheduling(report)) {
    stores = stores.filter((s) => s.scheduling_enabled !== false);
  }
  if (report?.requiredStoreFlag) {
    stores = stores.filter((s) => s[report.requiredStoreFlag]);
  }
  if (report?.requireActiveStore) {
    stores = stores.filter(
      (s) => s.status === "open" && s.onboarding_status === "active"
    );
  }
  return stores;
}
